import { ChildFragmentFragment } from "@outschool/gql-frontend-generated";
import { useTranslation } from "@outschool/localization";
import { useSession } from "@outschool/ui-auth";
import { useIsMobile } from "@outschool/ui-utils";
import React from "react";

import { truncateLearnerName } from "../components/search/shared/learnerSelector/LearnerSelectorConstants";
import { useActiveLearner } from "../providers/ActiveLearnerProvider";
import useLearnerSpecificSearchV4Experiment from "./useLearnerSpecificSearchV4Experiment";

interface UseSearchByLearnerArgs {
  onFilterChange?: (learner: ChildFragmentFragment | null) => void;
}

/**
 * Extension to the ActiveLearnerProvider that adds additional functionality for the search by learner feature.
 */
export function useSearchByLearner({
  onFilterChange,
}: UseSearchByLearnerArgs = {}) {
  const { currentUser } = useSession();
  const { t } = useTranslation("client\\providers\\SearchByLearnerProvider");
  const isMobile = useIsMobile();
  const { variant } = useLearnerSpecificSearchV4Experiment();

  const { activeLearner, setActiveLearner, showLearnerFilter } =
    useActiveLearner({
      onActiveLearnerChanged: onFilterChange,
    });

  const setLearner = React.useCallback(
    (learner: ChildFragmentFragment | null) => {
      setActiveLearner(learner?.uid ?? null);
    },
    [setActiveLearner],
  );

  const getSearchBarPlaceholder = (learner: ChildFragmentFragment | null) => {
    // In the control group — no user personalization at all.
    if (variant !== "treatment") {
      return t("What’s your child’s interest?");
    }

    // Treatment group — user personalization based on the learner.
    if (currentUser && currentUser?.children?.length > 0 && learner) {
      if (learner.name) {
        const truncatedName = truncateLearnerName(learner.name);
        if (isMobile) {
          return t("Find {{learnerName}}’s next class", {
            learnerName: truncatedName,
          });
        }
        return t("Find {{learnerName}}’s next class", {
          learnerName: truncateLearnerName(learner.name),
        });
      } else {
        return t("What’s your child’s interest?");
      }
    } else {
      return t("What’s your child’s interest?");
    }
  };

  // For control and holdout groups, remove all learner specific search functionality
  if (variant !== "treatment") {
    return {
      showLearnerFilter: false,
      learner: null,
      setLearner: () => {},
      searchBarPlaceholder: getSearchBarPlaceholder(null),
      getSearchBarPlaceholder,
    };
  }

  const searchBarPlaceholder = getSearchBarPlaceholder(activeLearner);

  return {
    showLearnerFilter,
    learner: activeLearner,
    setLearner,
    searchBarPlaceholder,
    getSearchBarPlaceholder,
  };
}
